import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { format } from "date-fns";
import AssignmentFilters from "./AssignmentFilters";
import AssignmentPagination from "../Assign_Course/list_of_assignments/AssignmentPagination";
import {
    Calendar,
    Clock,
    Users,
    Search as SearchIcon,
    Trash,
    Check
} from "lucide-react";
import '../../Final_Css/positiveflowbuttons.css';
import CancelConfirmDialog from "../Assign_Course/dialogs/CancelConfirmDialog";
import CancelSuccessDialog from "../Assign_Course/dialogs/CancelSuccessDialog";
import axios from "axios";
import Dialogs from "../../Reports/Dialogs";
import AttemptDropdown from "../../Reports/AttemptDropdown";

const PRIMARY = "#0191c8";

export default function EmployeeSlotsTable() {
    const navigate = useNavigate();
    const location = useLocation();

    const [showAddModal, setShowAddModal] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectedNewEmployeeIds, setSelectedNewEmployeeIds] = useState([]);
    const [selectedNewEmployees, setSelectedNewEmployees] = useState([]);
    // const [searchTerm, setSearchTerm] = useState("");
    const [selectedDept, setSelectedDept] = useState("");

    const [slots, setSlots] = useState([]);
    const [examDetails, setExamDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [error, setError] = useState(null);

    // Department and employee states
    const [departments, setDepartments] = useState([]);
    const [loadingDepartments, setLoadingDepartments] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [rescheduleOpen, setRescheduleOpen] = useState(false);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [newDateTime, setNewDateTime] = useState("");
    const [slotRows, setSlotRows] = useState([]);
    const [rescheduleRows, setRescheduleRows] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [employeeSearch, setEmployeeSearch] = useState(""); // employee popup search
    const [filterRule, setFilterRule] = useState(null);
    const [showFilters, setShowFilters] = useState(false);

    const [slotsPage, setSlotsPage] = useState(1);
    const [reschedulePage, setReschedulePage] = useState(1);
    const [slotsAttempt, setSlotsAttempt] = useState("all");
    const [rescheduleAttempt, setRescheduleAttempt] = useState("all");
    const pageSize = 10;
    const [slotsTotalCount, setSlotsTotalCount] = useState(0);
    const [rescheduleTotalCount, setRescheduleTotalCount] = useState(0);
    const [slotSelectableRows, setSlotSelectableRows] = useState([]);
    const [rescheduleSelectableRows, setRescheduleSelectableRows] = useState([]);

    // Tabs state
    const [activeTab, setActiveTab] = useState('changeSlot');

    // Delete modal states
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [employeeToDelete, setEmployeeToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [selectedSlotEmployees, setSelectedSlotEmployees] = useState([]);
    const [selectedRescheduleEmployees, setSelectedRescheduleEmployees] = useState([]);
    const [batchEmployees, setBatchEmployees] = useState([]);
    const selectedEmployees = activeTab === "changeSlot"
        ? selectedSlotEmployees
        : selectedRescheduleEmployees;
    const [rescheduleType, setRescheduleType] = useState("individual");
    const [newEndDateTime, setNewEndDateTime] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const {
        examId,
        examTitle,
        mode = "view"
    } = location.state || {};

    const isViewMode = mode === "view";

    // Cancel flow dialogs
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [cancelSuccessOpen, setCancelSuccessOpen] = useState(false);
    const [addCancelConfirmOpen, setAddCancelConfirmOpen] = useState(false);

    const handleGoBack = () => {
        navigate(-1);
    };

    const formatLocalDateTime = (date) => {
        const pad = (n) => String(n).padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    // Fetch data
    const fetchAssignmentDetails = async (table = "all", isInitialLoad = false) => {
        try {
            if (isInitialLoad)
                setLoading(true);
            else
                setTableLoading(true);
            setError(null);
            const fetchTable = async (assignmentType, page, attempt) => {
                const params = new URLSearchParams({
                    exam_id: examId,
                    assignment_type: assignmentType,
                    attempt,
                    page,
                    page_size: pageSize,
                    search: debouncedSearch
                });
                if (filterRule?.rules?.length) {
                    params.append("filters", JSON.stringify(filterRule));
                }
                const response = await axios.get(
                    `/coordinators/all_assignments/?${params.toString()}`,
                    { withCredentials: true }
                );
                return response.data;
            };

            const [slotsData, rescheduleData] = await Promise.all([
                table !== "reschedule" ? fetchTable("slots", slotsPage, slotsAttempt) : Promise.resolve(null),
                table !== "slots" ? fetchTable("reschedule", reschedulePage, rescheduleAttempt) : Promise.resolve(null)
            ]);
            if (slotsData) {
                setSlotRows(slotsData.rows || []);
                setSlotsTotalCount(slotsData.totalCount || 0);
                setSlotSelectableRows(slotsData.selectableRows || []);
            }
            if (rescheduleData) {
                setRescheduleRows(rescheduleData.rows || []);
                setRescheduleTotalCount(rescheduleData.totalCount || 0);
                setRescheduleSelectableRows(rescheduleData.selectableRows || []);
            }
            const detailsData = slotsData || rescheduleData;
            setExamDetails({
                examId: examId,
                title: detailsData.examTitle,
                examType: detailsData.examType
            });
        } catch (err) {
            console.error("Error:", err);
            setError(err.message || "Failed to load assessment slots");
        } finally {
            if (isInitialLoad)
                setLoading(false);
            else
                setTableLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500); // wait 500ms after user stops typing

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Fetch departments
    const fetchDepartments = async () => {
        try {
            setLoadingDepartments(true);
            const response = await axios.get('/coordinators/departments-list/', { withCredentials: true });
            const data = response.data;
            if (data.success && data.data) {
                const deptOptions = data.data.map(dept => ({
                    value: dept.DepartmentGUID || dept.id || dept.DepartmentId || dept.department_id,
                    label: dept.Name || dept.DepartmentName || dept.department_name || "Unknown Department"
                }));
                setDepartments(deptOptions);
            }
        } catch (error) {
            console.error("Error fetching departments:", error);
            setDepartments([]);
        } finally {
            setLoadingDepartments(false);
        }
    };

    // Fetch employees
    const fetchEmployees = async (departmentId) => {
        console.log("fetchEmployees called");
        try {
            setLoadingEmployees(true);
            const currentExamId = examId;
            console.log(currentExamId, "examid")

            if (!currentExamId || currentExamId === "N/A") {
                setEmployees([]);
                return;
            }
            let url = `/coordinators/employees-list/?exam_id=${currentExamId}`;
            console.log("Selected Department:", departmentId);
            console.log("Exam ID:", examId);
            console.log("URL:", url);

            if (departmentId) url += `&department_id=${departmentId}`;
            const response = await axios.get(url, { withCredentials: true });
            const data = response.data;
            if (data.success && data.data) {
                const employeeList = data.data.map((emp, index) => ({
                    id: emp.GUID || emp.Id || `emp-${index}`,
                    name: emp.EmployeeName || emp.employee_name || "Unknown Employee",
                    empId: emp.EmployeeId || emp.employee_id || `ID-${index}`,
                    department: emp.DepartmentName || emp.department_name || "Unknown",
                    departmentId: emp.DepartmentId || emp.department_id,
                    status: emp.AssessmentStatus || emp.status || "Available",
                    attemptCount: emp.UserAttemptCount ?? emp.userAttemptCount ?? emp.AttemptCount ?? 0,
                    guid: emp.GUID,
                    localId: emp.Id
                }));
                setEmployees(employeeList);
            } else {
                setEmployees([]);
            }
        } catch (error) {
            console.error("Error fetching employees:", error);
            setEmployees([]);
        } finally {
            setLoadingEmployees(false);
        }
    };

    // useEffect(() => {
    //     fetchAssignmentDetails();
    //     fetchDepartments();
    // }, [examId]);
    useEffect(() => {

        fetchAssignmentDetails("all", true);
        fetchDepartments();

    }, [examId]);

    useEffect(() => {
        fetchAssignmentDetails();
    }, [
        slotsPage,
        reschedulePage,
        slotsAttempt,
        rescheduleAttempt,
        debouncedSearch,
        filterRule
    ]);

    useEffect(() => {
        const selectableIds = new Set(slotSelectableRows.map(row => row.id));
        setSelectedSlotEmployees(previous => previous.filter(row => selectableIds.has(row.id)));
    }, [slotSelectableRows]);

    useEffect(() => {
        const selectableIds = new Set(rescheduleSelectableRows.map(row => row.id));
        setSelectedRescheduleEmployees(previous => previous.filter(row => selectableIds.has(row.id)));
    }, [rescheduleSelectableRows]);

    useEffect(() => {
        setSlotsPage(1);
        setReschedulePage(1);
    }, [debouncedSearch, filterRule]);

    useEffect(() => {
        if (examId) {
            fetchEmployees(selectedDept);

        }
    }, [examId, selectedDept]);

    // Flatten all employees from all slots for the table
    const allEmployees = slots.flatMap(slot =>
        slot.employees.map(emp => ({
            ...emp,
            slotStart: slot.start,
            slotEnd: slot.end,
            slotId: slot.id,
        }))
    );

    const availableEmployees = employees.filter(e => !allEmployees.some(emp => emp.employee_guid === e.id));

    const filteredEmployees = availableEmployees
        .filter(e => !selectedDept || String(e.departmentId) === String(selectedDept))
        .filter(e => {
            if (!employeeSearch) return true;
            const term = employeeSearch.toLowerCase();
            return [
                e.name,
                e.empId,
                e.department,
                e.status,
                e.attemptCount
            ].some(value => String(value || "").toLowerCase().includes(term));
        });

    const handleSelectEmployee = (id) => {
        setSelectedNewEmployeeIds(previous =>
            previous.includes(id)
                ? previous.filter(employeeId => employeeId !== id)
                : [...previous, id]
        );
    };

    const handleBookSlot = () => {
        const employeesToAdd = employees
            .filter(employee => selectedNewEmployeeIds.includes(employee.id))
            .map(employee => ({
                id: employee.id,
                name: employee.name,
                employeeGuid: employee.guid,
                action: "add",
            }));
        if (!employeesToAdd.length) return;
        setSelectedNewEmployees(employeesToAdd);
        setSelectedEmployee(employeesToAdd[0]);
        setRescheduleType("add");
        setNewDateTime("");
        setShowAddModal(false);
        setRescheduleOpen(true);

        setDropdownOpen(false);
        setSearchTerm("");
        setEmployeeSearch("");
        setSelectedNewEmployeeIds([]);
        setSelectedDept("");
    };

    const openDeleteModal = (examEmployeeId) => {
        setEmployeeToDelete(examEmployeeId);
        setShowDeleteModal(true);
    };

    const confirmDeleteEmployee = async () => {
        if (!employeeToDelete) return;
        setDeleting(true);
        try {
            const res = await axios.put(
                "/coordinators/deactivate-exam-employee/",
                { examEmployeeId: employeeToDelete },
                {
                    headers: { "Content-Type": "application/json" },
                    withCredentials: true
                }
            );
            if (res.status >= 200 && res.status < 300) {
                setShowDeleteModal(false);
                await fetchAssignmentDetails();
            } else {
                alert("Failed to remove employee");
            }
        } catch (err) {
            alert("Network error");
        } finally {
            setDeleting(false);
        }
    };

    useEffect(() => {
        if (!successDialogOpen && rescheduleType === "batch") {
            setSelectedSlotEmployees([]);
            setSelectedRescheduleEmployees([]);
            setBatchEmployees([]);
        }
    }, [successDialogOpen]);

    const handleChangeSlot = (emp, slotStart, slotEnd) => {
        if (!slotStart || !slotEnd) {
            alert("Could not find current slot for this employee");
            return;
        }
        console.log("Navigating to slots", {
            assignId: emp.assignId,
            examEmployeeId: emp.examEmployeeId,
            employeeName: emp.employeeName,
            examId
        });
        navigate("/hr/slots", {
            state: {
                mode: "edit",
                assignId: emp.assignId,
                examEmployeeId: emp.examEmployeeId,
                employeeName: emp.employeeName,
                prefillDate: slotStart,
                prefillTime: format(slotStart, "HH:mm"),
                examId
            }
        });
    };

    const handleCancelConfirmed = () => {
        setCancelConfirmOpen(false);
        setCancelSuccessOpen(true);
    };

    const handleCancelSuccessClose = () => {
        setCancelSuccessOpen(false);
    };

    const handleAddCancelConfirmed = () => {
        setAddCancelConfirmOpen(false);
        setShowAddModal(false);
        setDropdownOpen(false);
        setSearchTerm("");
        setEmployeeSearch("");
        setSelectedNewEmployeeIds([]);
        setSelectedNewEmployees([]);
        setSelectedDept("");
    };

    return (
        <div style={{
            minHeight: "100vh",
            background: "#f9fafb",
            fontFamily: "system-ui, sans-serif",
            padding: "0.75rem"
        }}>
            {loading ? (
                <div className="pd-loading-container">
                    <div className="pd-loading-animation">
                        <img className="pd-loading-video" src="/images/breath1.gif" alt="Loading..." />
                    </div>
                </div>
            ) : error ? (
                <div className="pd-no-data" style={{ color: "red", padding: "2rem 1rem", textAlign: "center" }}>
                    Error: {error}
                </div>
                // ) : rows.length === 0 ? (
                //     <div className="pd-no-data" style={{ padding: "2rem 1rem", textAlign: "center" }}>
                //         No assigned assessments found
                //     </div>
            ) : (
                <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "1rem 0.75rem" }}>
                    {/* HEADER with title and Add button */}
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: "1rem",
                        marginBottom: "0.5rem"
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                            <button
                                type="button"
                                className="pd-back-button"
                                onClick={handleGoBack}
                                aria-label="Go back"
                            >
                                <i className="fa fa-arrow-circle-left" aria-hidden="true"></i>
                            </button>
                            <h1 style={{
                                fontSize: "clamp(1.5rem, 4vw, 1.9rem)",
                                fontWeight: 800,
                                color: "#1f2937",
                                margin: 0
                            }}>
                                Assessment Details
                            </h1>
                        </div>
                        <button
                            className="btn"
                            onClick={() => {
                                if (isViewMode) return;
                                setEmployeeSearch("");
                                setSelectedDept("");
                                setSelectedNewEmployeeIds([]);
                                setSelectedNewEmployees([]);
                                setShowAddModal(true);
                            }}
                            disabled={isViewMode}
                            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
                        >
                            <Users size={22} />
                            Add New Employee
                        </button>
                    </div>

                    {/* Exam Title only */}
                    <div style={{ marginBottom: "1.5rem", paddingLeft: "0.25rem" }}>
                        <h2 style={{
                            fontSize: "clamp(1.2rem, 3vw, 1.5rem)",
                            fontWeight: 600,
                            color: "#1f2937",
                            margin: 0
                        }}>
                            {examDetails.title}
                        </h2>
                    </div>

                    {/* TABS */}
                    <div style={{ display: "flex", gap: "0.5rem", borderBottom: "2px solid #e5e7eb", marginBottom: "1.5rem" }}>
                        <button
                            onClick={() => setActiveTab('changeSlot')}
                            style={{
                                padding: "0.75rem 1.5rem",
                                fontSize: "1rem",
                                fontWeight: 600,
                                color: activeTab === 'changeSlot' ? PRIMARY : "#6b7280",
                                border: "none",
                                background: "transparent",
                                borderBottom: activeTab === 'changeSlot' ? `3px solid ${PRIMARY}` : "3px solid transparent",
                                cursor: "pointer",
                                transition: "all 0.2s",
                            }}
                        >
                            Assignments
                        </button>
                        <button
                            onClick={() => setActiveTab('reschedule')}
                            style={{
                                padding: "0.75rem 1.5rem",
                                fontSize: "1rem",
                                fontWeight: 600,
                                color: activeTab === 'reschedule' ? PRIMARY : "#6b7280",
                                border: "none",
                                background: "transparent",
                                borderBottom: activeTab === 'reschedule' ? `3px solid ${PRIMARY}` : "3px solid transparent",
                                cursor: "pointer",
                                transition: "all 0.2s",
                            }}
                        >
                            Reschedule Assignments
                        </button>
                    </div>
                    <AssignmentFilters
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        filterRule={filterRule}
                        setFilterRule={setFilterRule}
                        showFilters={showFilters}
                        setShowFilters={setShowFilters}
                    />

                    {/* TAB CONTENT */}
                    {activeTab === 'changeSlot' && (
                        <div style={{ background: "white", borderRadius: "12px", boxShadow: "0 4px 6px rgba(0,0,0,0.05)", padding: "1.5rem" }}>
                            {isViewMode && (
                                <div
                                    style={{
                                        width: "100%",
                                        textAlign: "center",
                                        padding: "10px 16px",
                                        marginBottom: "16px",
                                        backgroundColor: "#fff8e1",
                                        border: "1px solid #f59e0b",
                                        borderRadius: "6px",
                                        color: "#92400e",
                                        fontSize: "14px",
                                        fontWeight: 500,
                                    }}
                                >
                                    🔒 View-only mode. You can view assignment details only.
                                </div>
                            )}
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", marginBottom: "1rem" }}>

                                <h3 style={{ fontSize: "1.2rem", fontWeight: 700, color: "#1f2937", marginBottom: "1rem" }}>
                                    Slots List
                                </h3>
                                <div style={{ marginBottom: "1rem", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                    <AttemptDropdown
                                        value={slotsAttempt}
                                        onChange={(value) => { setSlotsAttempt(value); setSlotsPage(1); }}
                                        latestLabel="Latest Attempts"
                                    />
                                    <button
                                        className="btn"
                                        disabled={selectedEmployees.length === 0}
                                        onClick={() => {
                                            setBatchEmployees(selectedSlotEmployees);
                                            setRescheduleType("batch");
                                            setSelectedEmployee(null);
                                            setNewDateTime("");
                                            setNewEndDateTime("");
                                            setRescheduleOpen(true);
                                        }}
                                    >
                                        Batch Reschedule ({selectedEmployees.length})
                                    </button>
                                </div>
                            </div>
                            <div style={{ overflowX: "auto" }}>
                                <table
                                    className="pd-table"
                                    style={{
                                        tableLayout: "fixed",
                                        width: "100%",
                                        minWidth: "800px",
                                        textAlign: "left"
                                    }}
                                >
                                    <thead className="table-light">
                                        <tr>
                                            <th>
                                                <input
                                                    type="checkbox"
                                                    disabled={isViewMode}
                                                    checked={
                                                        slotSelectableRows.length > 0 &&
                                                        selectedSlotEmployees.length === slotSelectableRows.length
                                                    }
                                                    ref={(checkbox) => {
                                                        if (checkbox) checkbox.indeterminate = selectedSlotEmployees.length > 0
                                                            && selectedSlotEmployees.length < slotSelectableRows.length;
                                                    }}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            const selectable = slotSelectableRows
                                                                .filter(r => r.status !== "Completed" && r.status !== "Ongoing")
                                                                .map(r => ({
                                                                    id: r.examEmployeeId,
                                                                    name: r.employeeName,
                                                                    employeeGuid: r.employeeGuid,  // ✅ This is the correct field name
                                                                    assignId: r.assignId
                                                                }));
                                                            console.log("Selected employees data:", selectable); // Debug log
                                                            setSelectedSlotEmployees(selectable);
                                                        } else {
                                                            setSelectedSlotEmployees([]);
                                                        }
                                                    }}
                                                />
                                            </th>
                                            <th style={{ width: "5%" }}>S.No.</th>
                                            <th style={{ width: "15%" }}>Employee Name</th>
                                            <th style={{ width: "15%" }}>Department</th>
                                            <th style={{ width: "12%" }}>Assessment Number</th>
                                            <th style={{ width: "2%" }}>Attempt Count</th>
                                            <th style={{ width: "12%" }}>Assigned By</th>
                                            <th style={{ width: "18%" }}>Schedule Date & Time</th>
                                            <th style={{ width: "11%" }}>Slot Details</th>
                                            <th style={{ width: "12%" }}>Status</th>
                                            <th style={{ width: "20%" }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {slotRows.length > 0 ? (
                                            slotRows.map((row, index) => (
                                                <tr key={row.examEmployeeId || index}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedSlotEmployees.some(e => e.id === row.examEmployeeId)}
                                                            disabled={row.status === "Completed" || row.status === "Ongoing" || isViewMode}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    const newEmployee = {
                                                                        id: row.examEmployeeId,
                                                                        name: row.employeeName,
                                                                        employeeGuid: row.employeeGuid  // ✅ This is the correct field name
                                                                    };
                                                                    console.log("Adding employee:", newEmployee); // Debug log
                                                                    setSelectedSlotEmployees(prev => prev.some(item => item.id === newEmployee.id) ? prev : [...prev, newEmployee]);
                                                                } else {
                                                                    setSelectedSlotEmployees(prev =>
                                                                        prev.filter(e => e.id !== row.examEmployeeId)
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ textAlign: "start", verticalAlign: "middle" }}>{(slotsPage - 1) * pageSize + index + 1}</td>
                                                    <td>
                                                        <span style={{ fontWeight: 600 }}>
                                                            {row.employeeName || "Unknown"}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: "start", verticalAlign: "middle" }}>{row.departmentName || "-"}</td>
                                                    <td style={{ textAlign: "start", verticalAlign: "middle" }}>
                                                        {row.assignmentNumber || "N/A"}
                                                    </td>
                                                    <td style={{ textAlign: "start", verticalAlign: "middle" }}>
                                                        {row.userAttemptCount != null ? row.userAttemptCount : "-"}
                                                    </td>
                                                    <td>
                                                        {row.assignedBy || "N/A"}
                                                    </td>
                                                    <td style={{ textAlign: "start", verticalAlign: "middle" }}>
                                                        {row.scheduledDate
                                                            ? format(new Date(row.scheduledDate), "MMM d, yyyy hh:mm a")
                                                            : "—"}
                                                    </td>
                                                    <td style={{ textAlign: "start", verticalAlign: "middle" }}>
                                                        <div style={{
                                                            display: "flex", flexDirection: "row", justifyContent: "flex-start",
                                                            gap: "8px",
                                                            whiteSpace: "nowrap", alignItems: "center"
                                                        }}>
                                                            <span style={{ width: "150px", display: "inline-block", fontSize: "0.9rem", fontWeight: 500 }}>
                                                                {row.slotStart && row.slotEnd
                                                                    ? `${format(new Date(row.slotStart), "hh:mm a")} – ${format(new Date(row.slotEnd), "hh:mm a")}`
                                                                    : "—"}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td style={{ textAlign: "left", verticalAlign: "middle" }}>
                                                        <span
                                                            style={{
                                                                padding: "6px 16px",
                                                                borderRadius: "20px",
                                                                fontWeight: 500,
                                                                display: "inline-block",
                                                                minWidth: "90px",
                                                                textAlign: "center",
                                                                background:
                                                                    row.status === "Pending"
                                                                        ? "#fff3cd"
                                                                        : row.status === "Missed"
                                                                            ? "#f8d7da"
                                                                            : row.status === "Completed"
                                                                                ? "#d4edda"
                                                                                : row.status === "Ongoing"
                                                                                    ? "#ede9fe"
                                                                                    : "#f3f4f6",
                                                                color:
                                                                    row.status === "Pending"
                                                                        ? "#856404"
                                                                        : row.status === "Missed"
                                                                            ? "#721c24"
                                                                            : row.status === "Completed"
                                                                                ? "#155724"
                                                                                : row.status === "Ongoing"
                                                                                    ? "#5b21b6"
                                                                                    : "#374151"
                                                            }}
                                                        >
                                                            {row.status}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                                        <div style={{
                                                            display: "flex",
                                                            gap: "0.5rem",
                                                            justifyContent: "flex-start",
                                                            alignItems: "center",
                                                            flexWrap: "nowrap"
                                                        }}>
                                                            <button
                                                                onClick={() => {
                                                                    // setActiveTab('reschedule');
                                                                    setSelectedEmployee({
                                                                        id: row.examEmployeeId,
                                                                        name: row.employeeName,
                                                                        action: "reschedule"
                                                                    });
                                                                    setNewDateTime("");
                                                                    setRescheduleOpen(true);
                                                                }}
                                                                disabled={row.status === "Completed" || row.status === "Ongoing" || isViewMode}
                                                                style={{
                                                                    border: "none",
                                                                    background: "transparent",
                                                                    cursor: "pointer",
                                                                    padding: "4px"
                                                                }}
                                                            >
                                                                <img
                                                                    src="/images/reschedule.png"
                                                                    alt="Reschedule"
                                                                    style={{
                                                                        width: "40px",
                                                                        height: "40px"
                                                                    }}
                                                                />
                                                            </button>
                                                            <button
                                                                className="btn-negative"
                                                                onClick={() => openDeleteModal(row.examEmployeeId)}
                                                                // disabled={row.status === "Completed" || isViewMode}
                                                                disabled={
                                                                    row.status === "Completed" ||
                                                                    row.status === "Ongoing" ||
                                                                    isViewMode
                                                                }
                                                                style={{
                                                                    padding: "0.375rem 0.75rem",
                                                                    minWidth: "36px"
                                                                }}
                                                            >
                                                                <Trash className="trash" size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="11" style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}>
                                                    No employees assigned yet
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === 'reschedule' && (
                        <div style={{ background: "white", borderRadius: "12px", boxShadow: "0 4px 6px rgba(0,0,0,0.05)", padding: "1.5rem" }}>
                            {isViewMode && (
                                <div
                                    style={{
                                        width: "100%",
                                        textAlign: "center",
                                        padding: "10px 16px",
                                        marginBottom: "16px",
                                        backgroundColor: "#fff8e1",
                                        border: "1px solid #f59e0b",
                                        borderRadius: "6px",
                                        color: "#92400e",
                                        fontSize: "14px",
                                        fontWeight: 500,
                                    }}
                                >
                                    🔒 View-only mode. You can view assignment details only.
                                </div>
                            )}
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", marginBottom: "1rem" }}>
                                <h3 style={{ fontSize: "1.2rem", fontWeight: 700, color: "#1f2937", marginBottom: "1rem" }}>
                                    Rescheduled List
                                </h3>
                                <div style={{ marginBottom: "1rem", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                    <AttemptDropdown
                                        value={rescheduleAttempt}
                                        onChange={(value) => { setRescheduleAttempt(value); setReschedulePage(1); }}
                                        latestLabel="Latest Attempts"
                                    />
                                    {/* <button
                                        className="btn"
                                        disabled={selectedEmployees.length === 0} */}
                                    <button
                                        className="btn"
                                        disabled={selectedRescheduleEmployees.length === 0}
                                        onClick={() => {
                                            setBatchEmployees(selectedRescheduleEmployees);
                                            setRescheduleType("batch");
                                            setSelectedEmployee(null);
                                            setNewDateTime("");
                                            setNewEndDateTime("");
                                            setRescheduleOpen(true);
                                        }}
                                    >
                                        Batch Reschedule ({selectedEmployees.length})
                                    </button>
                                </div>
                            </div>
                            <div style={{ overflowX: "auto" }}>
                                <table className="pd-table" style={{ width: "100%", minWidth: "600px" }}>
                                    <thead className="table-light">
                                        <tr>
                                            <th>
                                                <input
                                                    type="checkbox"
                                                    disabled={isViewMode}
                                                    checked={
                                                        rescheduleSelectableRows.length > 0 &&
                                                        selectedRescheduleEmployees.length === rescheduleSelectableRows.length
                                                    }
                                                    ref={(checkbox) => {
                                                        if (checkbox) checkbox.indeterminate = selectedRescheduleEmployees.length > 0
                                                            && selectedRescheduleEmployees.length < rescheduleSelectableRows.length;
                                                    }}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            const selectable = rescheduleSelectableRows
                                                                .filter(r => r.status !== "Completed" && r.status !== "Ongoing")
                                                                .map(r => ({
                                                                    id: r.examEmployeeId,
                                                                    name: r.employeeName,
                                                                    employeeGuid: r.employeeGuid  // ✅ This is the correct field name
                                                                }));
                                                            console.log("Selected reschedules data:", selectable); // Debug log
                                                            setSelectedRescheduleEmployees(selectable);
                                                        } else {
                                                            setSelectedRescheduleEmployees([]);
                                                        }
                                                    }}

                                                />
                                            </th>
                                            <th style={{ width: "5%" }}>S.No.</th>
                                            <th style={{ width: "15%" }}>Employee Name</th>
                                            <th style={{ width: "15%" }}>Department</th>
                                            <th style={{ width: "12%" }}>Assessment Number</th>
                                            <th style={{ width: "2%" }}>Attempt Count</th>
                                            <th style={{ width: "12%" }}>Assigned By</th>
                                            <th style={{ width: "18%" }}>Schedule Date & Time</th>
                                            <th style={{ width: "13%" }}>Slot Details</th>
                                            <th style={{ width: "12%" }}>Status</th>
                                            <th style={{ width: "20%" }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rescheduleRows.length > 0 ? (
                                            rescheduleRows.map((row, index) => (
                                                <tr key={row.examEmployeeId || index}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedRescheduleEmployees.some(e => e.id === row.examEmployeeId)}
                                                            disabled={row.status === "Completed" || row.status === "Ongoing" || isViewMode}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    const newEmployee = {
                                                                        id: row.examEmployeeId,
                                                                        name: row.employeeName,
                                                                        employeeGuid: row.employeeGuid  // ✅ This is the correct field name
                                                                    };
                                                                    console.log("Adding reschedule employee:", newEmployee); // Debug log
                                                                    setSelectedRescheduleEmployees(prev => prev.some(item => item.id === newEmployee.id) ? prev : [...prev, newEmployee]);
                                                                } else {
                                                                    setSelectedRescheduleEmployees(prev =>
                                                                        prev.filter(e => e.id !== row.examEmployeeId)
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ textAlign: "start", verticalAlign: "middle" }}>{(reschedulePage - 1) * pageSize + index + 1}</td>
                                                    <td>
                                                        <div style={{ display: "flex", alignItems: "center" }}>
                                                            <span style={{ fontWeight: 600 }}>
                                                                {row.employeeName}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td style={{ textAlign: "start", verticalAlign: "middle" }}>{row.departmentName || "-"}</td>
                                                    <td style={{ textAlign: "start", verticalAlign: "middle" }}>{row.assignmentNumber}</td>
                                                    <td style={{ textAlign: "start", verticalAlign: "middle" }}>
                                                        {row.userAttemptCount != null ? row.userAttemptCount : "-"}
                                                    </td>
                                                    <td style={{ textAlign: "start", verticalAlign: "middle" }}>{row.assignedBy || "System"}</td>
                                                    <td style={{ textAlign: "start", verticalAlign: "middle" }}>
                                                        {row.scheduledDate
                                                            ? format(new Date(row.scheduledDate), "MMM d, yyyy hh:mm a")
                                                            : "—"}
                                                    </td>
                                                    <td style={{ textAlign: "start", verticalAlign: "middle" }}>
                                                        <div style={{
                                                            display: "flex", flexDirection: "row", justifyContent: "flex-start",
                                                            gap: "8px",
                                                            whiteSpace: "nowrap", alignItems: "center"
                                                        }}>
                                                            <span
                                                                style={{
                                                                    width: "145px",
                                                                    display: "inline-block",
                                                                    whiteSpace: "nowrap",
                                                                    fontSize: "0.9rem",
                                                                    fontWeight: 500
                                                                }}
                                                            >
                                                                {row.slotStart && row.slotEnd
                                                                    ? `${format(new Date(row.slotStart), "hh:mm a")} – ${format(new Date(row.slotEnd), "hh:mm a")}`
                                                                    : "—"}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td style={{ textAlign: "left", verticalAlign: "middle" }}>
                                                        <span
                                                            style={{
                                                                padding: "6px 16px",
                                                                borderRadius: "20px",
                                                                fontWeight: 500,
                                                                display: "inline-block",
                                                                minWidth: "90px",
                                                                textAlign: "center",
                                                                background:
                                                                    row.status === "Pending"
                                                                        ? "#fff3cd"
                                                                        : row.status === "Missed"
                                                                            ? "#f8d7da"
                                                                            : row.status === "Completed"
                                                                                ? "#d4edda"
                                                                                : row.status === "Ongoing"
                                                                                    ? "#ede9fe"
                                                                                    : "#f3f4f6",
                                                                color:
                                                                    row.status === "Pending"
                                                                        ? "#856404"
                                                                        : row.status === "Missed"
                                                                            ? "#721c24"
                                                                            : row.status === "Completed"
                                                                                ? "#155724"
                                                                                : row.status === "Ongoing"
                                                                                    ? "#5b21b6"
                                                                                    : "#374151"
                                                            }}
                                                        >
                                                            {row.status}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: "left", verticalAlign: "middle" }}>
                                                        <div style={{
                                                            display: "flex",
                                                            gap: "0.5rem",
                                                            justifyContent: "flex-start",
                                                            alignItems: "center",
                                                            flexWrap: "nowrap"
                                                        }}>
                                                            <button
                                                                onClick={() => {
                                                                    // setActiveTab('reschedule');
                                                                    setRescheduleType("individual");
                                                                    setSelectedEmployee({
                                                                        id: row.examEmployeeId,
                                                                        name: row.employeeName
                                                                    });
                                                                    setNewDateTime("");
                                                                    setRescheduleOpen(true);
                                                                }}
                                                                disabled={row.status === "Completed" || row.status === "Ongoing" || isViewMode}
                                                                style={{
                                                                    border: "none",
                                                                    background: "transparent",
                                                                    cursor: "pointer",
                                                                    padding: "4px"
                                                                }}
                                                            >
                                                                <img
                                                                    src="/images/reschedule.png"
                                                                    alt="Reschedule"
                                                                    style={{
                                                                        width: "40px",
                                                                        height: "40px"
                                                                    }}
                                                                />
                                                            </button>
                                                            <button
                                                                className="btn-negative"
                                                                onClick={() => openDeleteModal(row.examEmployeeId)}
                                                                disabled={row.status === "Completed" || row.status === "Ongoing" || isViewMode}
                                                                style={{
                                                                    padding: "0.375rem 0.75rem",
                                                                    minWidth: "36px"
                                                                }}
                                                            >
                                                                <Trash className="trash" size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="11" style={{ textAlign: "center" }}>
                                                    No rescheduled entries found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {!loading && activeTab === "changeSlot" && slotsTotalCount > 0 && (
                        <AssignmentPagination
                            page={slotsPage}
                            totalPages={Math.ceil(slotsTotalCount / pageSize)}
                            handlePageChange={(newPage) => setSlotsPage(newPage)}
                        />
                    )}
                    {!loading && activeTab === "reschedule" && rescheduleTotalCount > 0 && (
                        <AssignmentPagination
                            page={reschedulePage}
                            totalPages={Math.ceil(rescheduleTotalCount / pageSize)}
                            handlePageChange={(newPage) => setReschedulePage(newPage)}
                        />
                    )}

                    {/* Add Modal - unchanged */}
                    {showAddModal && (
                        <>
                            <div
                                style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 999 }}
                                onClick={() => {
                                    setShowAddModal(false);
                                    setDropdownOpen(false);
                                    setSearchTerm("");
                                    setEmployeeSearch("");
                                    setSelectedNewEmployeeIds([]);
                                    setSelectedNewEmployees([]);
                                    setSelectedNewEmployeeIds([]);
                                    setSelectedDept("");
                                }}
                            />
                            <div style={{
                                position: "fixed",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                background: "white",
                                borderRadius: "20px",
                                padding: "clamp(1.5rem, 3vw, 2.5rem)",
                                width: "95%",
                                maxWidth: "560px",
                                maxHeight: "90vh",
                                overflowY: "auto",
                                boxShadow: "0 30px 80px rgba(0,0,0,0.35)",
                                zIndex: 1000,
                            }}>
                                <h3 style={{
                                    fontSize: "clamp(1.4rem, 3vw, 1.7rem)",
                                    fontWeight: 800,
                                    color: "#1f2937",
                                    margin: "0 0 0.6rem 0"
                                }}>
                                    Assign Assessment
                                </h3>
                                <p style={{ color: "#4b5563", marginBottom: "1.5rem", fontSize: "0.95rem" }}>
                                    Select one or more employees to assign the assessment slot.
                                </p>
                                {/* Department Dropdown */}
                                <div style={{ marginBottom: "1.5rem" }}>
                                    <label style={{ display: "block", fontSize: "0.95rem", fontWeight: 600, color: "#374151", marginBottom: "0.5rem" }}>
                                        Department
                                    </label>
                                    <select
                                        value={selectedDept}
                                        onChange={(e) => {
                                            setSelectedDept(e.target.value);
                                            setEmployeeSearch("");
                                            setSelectedNewEmployeeIds([]);
                                            setSelectedNewEmployees([]);
                                            setDropdownOpen(false);
                                        }}
                                        style={{
                                            width: "100%",
                                            padding: "0.875rem 1rem",
                                            borderRadius: "14px",
                                            border: "2px solid #e5e7eb",
                                            background: "white",
                                            fontSize: "0.95rem",
                                            outline: "none",
                                            cursor: "pointer",
                                        }}
                                        disabled={loadingDepartments}
                                    >
                                        <option value="">All Departments</option>
                                        {loadingDepartments ? (
                                            <option value="" disabled>Loading departments...</option>
                                        ) : (
                                            departments.map(dept => (
                                                <option key={dept.value} value={dept.value}>
                                                    {dept.label}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                                <div style={{ position: "relative", marginBottom: "1.5rem" }}>
                                    <div style={{ position: "relative" }}>
                                        <SearchIcon size={20} style={{
                                            position: "absolute",
                                            left: "1rem",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            color: "#6b7280"
                                        }} />
                                        <input
                                            type="text"
                                            placeholder={loadingEmployees ? "Loading employees..." : "Search employee..."}
                                            // value={searchTerm}
                                            value={employeeSearch}
                                            onChange={(e) => {
                                                // setSearchTerm(e.target.value);
                                                setEmployeeSearch(e.target.value);
                                                if (!dropdownOpen) setDropdownOpen(true);
                                            }}
                                            onFocus={() => !loadingEmployees && setDropdownOpen(true)}
                                            style={{
                                                width: "100%",
                                                padding: "0.875rem 1rem 0.875rem 2.8rem",
                                                borderRadius: "14px",
                                                border: dropdownOpen ? "2px solid #0191c8" : "2px solid #e5e7eb",
                                                fontSize: "0.95rem",
                                                outline: "none",
                                                background: loadingEmployees ? "#f9fafb" : "white",
                                                transition: "all 0.2s",
                                                boxShadow: dropdownOpen ? "0 0 0 4px rgba(1,145,200,0.15)" : "none",
                                                cursor: loadingEmployees ? "not-allowed" : "text",
                                            }}
                                            disabled={loadingEmployees}
                                        />
                                    </div>
                                    {dropdownOpen && !loadingEmployees && (
                                        <div style={{
                                            position: "static",
                                            background: "white",
                                            border: "2px solid #0191c8",
                                            borderRadius: "16px",
                                            marginTop: "8px",
                                            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                            overflow: "hidden",
                                            maxHeight: "260px",
                                        }}>
                                            <div style={{
                                                maxHeight: "280px",
                                                overflowY: "auto",
                                                padding: "0.5rem 0"
                                            }} className="modern-scrollbar">
                                                {filteredEmployees.length > 0 ? (
                                                    filteredEmployees.map((emp) => (
                                                        <div
                                                            key={emp.id}
                                                            onClick={() => handleSelectEmployee(emp.id)}
                                                            style={{
                                                                padding: "0.875rem 1rem",
                                                                cursor: "pointer",
                                                                background: selectedNewEmployeeIds.includes(emp.id) ? "#f0f9ff" : "transparent",
                                                                borderLeft: selectedNewEmployeeIds.includes(emp.id) ? `4px solid ${PRIMARY}` : "4px solid transparent",
                                                                transition: "all 0.2s",
                                                            }}
                                                            onMouseEnter={(e) => e.currentTarget.style.background = selectedNewEmployeeIds.includes(emp.id) ? "#f0f9ff" : "#f8fafc"}
                                                            onMouseLeave={(e) => e.currentTarget.style.background = selectedNewEmployeeIds.includes(emp.id) ? "#f0f9ff" : "transparent"}
                                                        >
                                                            <div style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "space-between",
                                                                gap: "0.75rem",
                                                                flexWrap: "wrap"
                                                            }}>
                                                                <div style={{ flex: 1, minWidth: "200px" }}>
                                                                    <div style={{
                                                                        fontWeight: 600,
                                                                        fontSize: "0.95rem",
                                                                        color: "#1f2937"
                                                                    }}>{emp.name}</div>
                                                                    <div style={{
                                                                        fontSize: "0.85rem",
                                                                        color: "#6b7280",
                                                                        marginTop: "2px"
                                                                    }}>
                                                                        ID: {emp.empId} • {emp.department}
                                                                    </div>
                                                                </div>
                                                                <div style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: "0.5rem",
                                                                    flexWrap: "wrap",
                                                                    justifyContent: "flex-end"
                                                                }}>
                                                                    <span style={{
                                                                        borderRadius: "999px",
                                                                        background: emp.status === "Available" ? "#ecfdf5" : "#eff6ff",
                                                                        color: emp.status === "Available" ? "#047857" : "#1d4ed8",
                                                                        fontSize: "0.75rem",
                                                                        fontWeight: 700,
                                                                        padding: "0.25rem 0.6rem",
                                                                        whiteSpace: "nowrap"
                                                                    }}>
                                                                        {emp.status}
                                                                    </span>
                                                                    <span style={{
                                                                        borderRadius: "999px",
                                                                        background: "#f8fafc",
                                                                        color: "#475569",
                                                                        fontSize: "0.75rem",
                                                                        fontWeight: 700,
                                                                        padding: "0.25rem 0.6rem",
                                                                        whiteSpace: "nowrap"
                                                                    }}>
                                                                        Attempts: {emp.attemptCount}
                                                                    </span>
                                                                    {selectedNewEmployeeIds.includes(emp.id) && <Check size={20} color={PRIMARY} />}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div style={{
                                                        padding: "2rem 1rem",
                                                        textAlign: "center",
                                                        color: "#9ca3af",
                                                        fontSize: "0.95rem"
                                                    }}>
                                                        {employees.length === 0 ? "No employees available" : "No matching employees found"}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div style={{ display: "flex", flexDirection: "row", gap: "0.75rem", marginTop: "1rem" }}>
                                    <button
                                        className="btn-negative"
                                        onClick={() => setAddCancelConfirmOpen(true)}
                                        style={{ padding: "0.875rem", width: "100%" }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn"
                                        onClick={handleBookSlot}
                                        disabled={selectedNewEmployeeIds.length === 0 || loadingEmployees}
                                        style={{ padding: "0.875rem", width: "100%" }}
                                    >
                                        {loadingEmployees ? "Loading..." : `Assign Exam (${selectedNewEmployeeIds.length})`}
                                    </button>
                                </div>
                            </div>
                            <style jsx>{`
                                .modern-scrollbar {
                                    scrollbar-width: thin;
                                    scrollbar-color: #94a3b8 #f8fafc;
                                }
                                .modern-scrollbar::-webkit-scrollbar {
                                    width: 8px;
                                }
                                .modern-scrollbar::-webkit-scrollbar-track {
                                    background: #f8fafc;
                                    border-radius: 10px;
                                }
                                .modern-scrollbar::-webkit-scrollbar-thumb {
                                    background: #94a3b8;
                                    border-radius: 10px;
                                    border: 2px solid #f8fafc;
                                }
                                .modern-scrollbar::-webkit-scrollbar-thumb:hover {
                                    background: #64748b;
                                }
                            `}</style>
                        </>
                    )}

                    {/* DELETE CONFIRMATION MODAL - unchanged */}
                    {showDeleteModal && (
                        <>
                            <div
                                style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 999 }}
                                onClick={() => !deleting && setShowDeleteModal(false)}
                            />
                            <div style={{
                                position: "fixed",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                background: "white",
                                borderRadius: "20px",
                                padding: "clamp(1.5rem, 3vw, 2.5rem)",
                                width: "90%",
                                maxWidth: "500px",
                                maxHeight: "90vh",
                                overflowY: "auto",
                                boxShadow: "0 30px 80px rgba(0,0,0,0.35)",
                                zIndex: 1000,
                                textAlign: "center",
                            }}>
                                <h3 style={{ fontSize: "clamp(1.3rem, 3vw, 1.6rem)", fontWeight: 700, color: "#1f2937", margin: "0 0 1rem" }}>
                                    Remove Employee?
                                </h3>
                                <p style={{ color: "#6b7280", fontSize: "clamp(0.95rem, 2vw, 1.05rem)", margin: "0 0 1.5rem" }}>
                                    This employee will be removed from this assessment slot.
                                </p>
                                <div style={{ display: "flex", flexDirection: "row", gap: "0.75rem", marginTop: "1rem" }}>
                                    <button
                                        className="btn-negative"
                                        onClick={() => setShowDeleteModal(false)}
                                        disabled={deleting}
                                        style={{ padding: "0.875rem", width: "100%" }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn"
                                        onClick={confirmDeleteEmployee}
                                        disabled={deleting}
                                        style={{ padding: "0.875rem", width: "100%" }}
                                    >
                                        {deleting ? "Removing..." : "Remove"}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    <CancelConfirmDialog
                        open={cancelConfirmOpen}
                        onClose={() => setCancelConfirmOpen(false)}
                        onConfirm={handleCancelConfirmed}
                    />
                    <CancelSuccessDialog
                        open={cancelSuccessOpen}
                        onClose={handleCancelSuccessClose}
                    />
                    <CancelConfirmDialog
                        open={addCancelConfirmOpen}
                        onClose={() => setAddCancelConfirmOpen(false)}
                        onConfirm={handleAddCancelConfirmed}
                        title="Cancel Booking?"
                        message="Any unsaved changes will be lost. Do you want to continue?"
                    />

                    <Dialogs
                        rescheduleOpen={rescheduleOpen}
                        setRescheduleOpen={setRescheduleOpen}
                        successDialogOpen={successDialogOpen}
                        setSuccessDialogOpen={setSuccessDialogOpen}
                        selectedEmployee={selectedEmployee}
                        selectedEmployees={rescheduleType === "add"
                            ? selectedNewEmployees
                            : rescheduleType === "batch"
                                ? batchEmployees
                                : selectedEmployees}
                        newDateTime={newDateTime}
                        setNewDateTime={setNewDateTime}
                        newEndDateTime={newEndDateTime}
                        setNewEndDateTime={setNewEndDateTime}
                        rescheduleType={rescheduleType}
                        setRescheduleType={setRescheduleType}
                        refreshReports={() => fetchAssignmentDetails(rescheduleType === "add" ? "slots" : "all")}
                        examId={examId}
                        onRescheduleSuccess={() => {
                            setActiveTab(rescheduleType === "add" ? "changeSlot" : "reschedule");
                        }}
                    />
                </div>
            )}
            <style jsx>{`
                @media (max-width: 768px) {
                    .pd-table {
                        font-size: 0.875rem;
                    }
                    .circle-badge {
                        width: 35px;
                        height: 35px;
                        font-size: 14px;
                    }
                }
                @media (max-width: 480px) {
                    .pd-table {
                        font-size: 0.8125rem;
                    }
                    .circle-badge {
                        width: 30px;
                        height: 30px;
                        font-size: 12px;
                        margin-right: 0.5rem;
                    }
                }
                @media (min-width: 768px) {
                    .pd-table {
                        font-size: 0.9375rem;
                    }
                }
            `}</style>
        </div>
    );
}
